/**
 * The assistant bubble's memory on this device, one entry per brand: whether
 * the panel was open and which conversation it was showing.
 *
 * Same manners as `newsFilterStorage.ts`: a versioned envelope, every read and
 * write wrapped, silence on failure. `localStorage` throws on quota and in some
 * private modes, and an older build may have written another shape; both end
 * in "closed, new chat" rather than an error. Its own namespace, so the
 * `outpost:draft:` sweeper never takes it.
 */

/** Bumped when the stored shape's meaning changes. Older payloads are dropped. */
const PREFS_VERSION = 1;

const PREFS_PREFIX = 'outpost:assistant';

export function assistantPrefsKey(businessId: string): string {
  return `${PREFS_PREFIX}:${businessId}`;
}

export interface AssistantPrefs {
  open: boolean;
  /** The conversation the bubble continues. Null = a new chat on the next send. */
  threadId: string | null;
}

export const DEFAULT_ASSISTANT_PREFS: AssistantPrefs = { open: false, threadId: null };

export function readAssistantPrefs(businessId: string): AssistantPrefs {
  try {
    const raw = window.localStorage.getItem(assistantPrefsKey(businessId));
    if (raw === null) return DEFAULT_ASSISTANT_PREFS;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return DEFAULT_ASSISTANT_PREFS;
    const { v, open, thread_id } = parsed as Record<string, unknown>;
    if (v !== PREFS_VERSION || typeof open !== 'boolean') return DEFAULT_ASSISTANT_PREFS;
    if (thread_id !== null && typeof thread_id !== 'string') return DEFAULT_ASSISTANT_PREFS;
    return { open, threadId: thread_id };
  } catch {
    return DEFAULT_ASSISTANT_PREFS;
  }
}

export function writeAssistantPrefs(businessId: string, prefs: AssistantPrefs): void {
  try {
    window.localStorage.setItem(
      assistantPrefsKey(businessId),
      JSON.stringify({ v: PREFS_VERSION, open: prefs.open, thread_id: prefs.threadId }),
    );
  } catch {
    // Not worth a message: the bubble just forgets on the next load.
  }
}
